import {
  Box,
  Button,
  CloseRoundedIcon,
  Divider,
  Link,
  MenuItem,
  MyLocationIcon,
  Typography,
  useTheme
} from '@hermes/web-components'
import { capitalise } from '../../../../utils/strings'
import { ItemRootContainer, ItemTitle, StyledMenuList, StyledNoOptionText } from '../styles'
import LoadingWheel from '../../../Icons/LoadingWheel'
import { MobileSearchTextField } from '../MobileView/styles'
import { Container } from './styles'
import useTranslateMessage from '../../../../hooks/useTranslateMessage'
import useHomePageSearchBarConfig from '../../../search/hooks/useHomePageSearchBarConfig'
import { FC, SetStateAction, useCallback, useContext, useRef, useState, useMemo } from 'react'
import TypeMenuItemBlock from './TypeMenuItemBlock'
import { SearchTypes } from '../../../../providers/SearchProfilesPage/types'
import { HomePageContext } from '../../../../providers/HomePage'
import {
  OnChangeLocationSearch,
  OnChangeSearch,
  OnChangeSearchType,
  OnSearchFieldBlur,
  SetIsSearchFieldFocused
} from '../../../SearchPage/common/types'
import { AppDataContext } from '../../../../providers/AppData'
import { Location } from '../../../../types'
import { useTimeout } from '../../../../hooks/useTimeout'
import CustomAutocomplete from '../CustomAutocomplete'
import useClickOutside from '../../../../hooks/useClickOutside'

interface Props {
  searchText: string
  isSearchFieldFocused: boolean
  onSearch: () => void
  onChangeLocationSearch: OnChangeLocationSearch
  onSearchFieldFocus: () => void
  onChangeSearch: OnChangeSearch
  onChangeSearchType: OnChangeSearchType
  setSearchText: (value: string) => void
  getYourLocation: () => void
  setIsSearchFieldFocused: SetIsSearchFieldFocused
  setIsLocationFieldFocused: SetIsSearchFieldFocused
  onSearchFieldBlur: OnSearchFieldBlur
  setSearchBarExpand: (value: boolean) => void
}

const baseAdornmentStyles = {
  marginLeft: '17px',
  marginRight: '17px',
  width: '16px',
  height: '16px',
  cursor: 'pointer'
}

const MobileFiltersModal: FC<Props> = ({
  searchText,
  isSearchFieldFocused: _isSearchFieldFocused,
  setIsSearchFieldFocused: _setIsSearchFieldFocused,
  onSearch,
  onChangeLocationSearch,
  onChangeSearch,
  onChangeSearchType,
  onSearchFieldFocus,
  getYourLocation,
  setSearchBarExpand,
  setIsLocationFieldFocused,
  onSearchFieldBlur,
  setSearchText
}) => {
  const timeout = useTimeout()
  const translate = useTranslateMessage()
  const theme = useTheme()
  const { language } = useContext(AppDataContext)
  const {
    changeCurrentLocation,
    resetKeywordSearch,
    resetValidation,
    state: {
      filters,
      isKeywordChanged,
      isLocationChanged,
      locations,
      isKeywordValidationFailed,
      isLocationValidationFailed,
      isLoadingButtonState
    }
  } = useContext(HomePageContext)

  const {
    searchTypes,
    selectedSearchType,
    MobileProfileTypeLeftIcon,
    MobileProfileTypeRightIcon,
    MobileKeywordSearchLeftIconAlternative,
    MobileLocationSearchLeftIconAlternative
  } = useHomePageSearchBarConfig()

  const [isLocationFocused, setIsLocationFocused] = useState(false)
  const [openSearchTypeDropdown, setOpenSearchTypeDropdown] = useState(false)

  const [searchFieldFocused, setSearchFieldFocused] = useState(true)
  const setIsSearchFieldFocused = useCallback(
    (v: SetStateAction<boolean>) => {
      _setIsSearchFieldFocused(v)
      setSearchFieldFocused(v)
    },
    [_setIsSearchFieldFocused]
  )
  const isSearchFieldFocused = useMemo(
    () => searchFieldFocused || _isSearchFieldFocused,
    [_isSearchFieldFocused, searchFieldFocused]
  )

  const searchTypeRef = useRef(null)
  const searchFieldRef = useRef(null)
  const locationFieldRef = useRef(null)

  useClickOutside(searchTypeRef, () => setOpenSearchTypeDropdown(false))
  useClickOutside(searchFieldRef, () => setIsSearchFieldFocused(false))
  useClickOutside(locationFieldRef, () => setIsLocationFocused(false))

  const onToggleModal = () => setOpenSearchTypeDropdown((prev) => !prev)
  const changeLocationFocus = () => setIsLocationFocused((prev) => !prev)
  const onClose = () => setOpenSearchTypeDropdown(false)

  const handleSearchType = useCallback(
    (value: SearchTypes) => () => {
      onClose()
      onChangeSearchType(value)
      setIsSearchFieldFocused(true)
    },
    [onChangeSearchType, setIsSearchFieldFocused]
  )

  const onLocationChanged = (newValue: { id: string; label: string; slug?: string | null }) =>
    timeout(() => {
      changeCurrentLocation(newValue.id, newValue.label, newValue.slug ?? undefined)
      setSearchText(newValue.label)
      setIsLocationFocused(false)
    })

  const autocompleteLocationMapper = ({ place_id, description, name, googlePlaceId, slug }: Location) => ({
    id: place_id || googlePlaceId,
    label: description || name[language],
    slug
  })

  const mappedLocation = locations?.map(autocompleteLocationMapper) || []

  const getSearchEndIcon = () => {
    if (isKeywordChanged) {
      return <LoadingWheel style={{ marginLeft: '10px' }} />
    }

    if (filters.searchString?.length && filters.searchType !== SearchTypes.dentist) {
      return (
        <CloseRoundedIcon
          sx={{ borderRadius: '50%', background: '#F4F5F5', padding: '4px', width: '24px', height: '24px' }}
          onClick={() => resetKeywordSearch()}
        />
      )
    }

    return null
  }

  return (
    <Container>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div ref={searchTypeRef} style={{ position: 'relative', marginBottom: openSearchTypeDropdown ? '16px' : 0 }}>
          <MobileSearchTextField
            fullWidth
            isFocused={openSearchTypeDropdown}
            isFilled={!!selectedSearchType}
            inputProps={{ 'aria-label': 'Mobile Search Text Field' }}
            onClick={() => {
              onToggleModal()
            }}
            placeholder={capitalise(selectedSearchType?.title)}
            value={
              openSearchTypeDropdown ? translate('homepage.searchbar.look') : capitalise(selectedSearchType?.title)
            }
            InputProps={{
              startAdornment: openSearchTypeDropdown ? null : MobileProfileTypeLeftIcon,
              endAdornment: (
                <MobileProfileTypeRightIcon
                  sx={{
                    color: 'inherit',
                    transition: 'all 0.3s ease-in-out',
                    rotate: openSearchTypeDropdown ? '180deg' : 0,
                    ...baseAdornmentStyles
                  }}
                />
              ),
              readOnly: true,
              sx: (themeIn) => ({ ...themeIn.typography.body1, padding: 0 })
            }}
          />
          <StyledMenuList style={{ zIndex: 99 }} isOpen={openSearchTypeDropdown}>
            {searchTypes.map(({ value, title, Icon }, index) => (
              <TypeMenuItemBlock
                key={title}
                value={value}
                title={title}
                isFirstItem={index === 0}
                isLastItem={searchTypes.length - 1 === index}
                searchType={filters.searchType}
                onClick={handleSearchType(value)}
                Icon={Icon}
              />
            ))}
          </StyledMenuList>
        </div>
        <div ref={searchFieldRef} style={{ position: 'relative', marginBottom: isSearchFieldFocused ? '16px' : 0 }}>
          <MobileSearchTextField
            fullWidth
            isError={isKeywordValidationFailed}
            isFilled={!!filters.selectedKeywordName}
            isFocused={isSearchFieldFocused}
            placeholder={translate('homepage.search.keyword.placeholder')}
            autoComplete="off"
            inputProps={{ 'aria-label': 'Mobile Search Text Field' }}
            sx={{ width: '100%', position: 'relative' }}
            value={filters.searchString}
            onChange={(e) => {
              const value = e.target.value
              onChangeSearch(e)

              if (!value?.length) {
                resetKeywordSearch()
              }
            }}
            onFocus={() => {
              if (!isKeywordChanged) {
                onSearchFieldFocus()
              }
            }}
            onBlur={onSearchFieldBlur}
            InputProps={{
              startAdornment: (
                <MobileKeywordSearchLeftIconAlternative
                  sx={{ path: { stroke: '#11245A', strokeWidth: '1.5' }, ...baseAdornmentStyles }}
                />
              ),
              endAdornment: getSearchEndIcon(),
              sx: (themeIn) => ({ ...themeIn.typography.body1, padding: '0 20px 0 0' })
            }}
          />
          {isSearchFieldFocused && (
            <CustomAutocomplete
              isSearchFieldFocused={isSearchFieldFocused}
              setIsSearchFieldFocused={(value) => {
                setIsSearchFieldFocused(value)
                setIsLocationFocused(true)
              }}
              onChangeSearch={onChangeSearch}
              onSearchFieldBlur={onSearchFieldBlur}
            />
          )}
        </div>
        <div style={{ position: 'relative' }} ref={locationFieldRef} onClick={() => setIsLocationFocused(true)}>
          <MobileSearchTextField
            fullWidth
            isError={isLocationValidationFailed}
            isFilled={!!searchText}
            value={searchText}
            isFocused={isLocationFocused}
            autoComplete="off"
            onClick={() => {
              resetValidation()
              changeLocationFocus()
            }}
            onFocus={changeLocationFocus}
            placeholder={translate('homepage.search.location.placeholder')}
            onChange={(e) => {
              setSearchText(e.target.value)
              onChangeLocationSearch(e)
            }}
            onBlur={() => setSearchText(decodeURIComponent(filters.selectedLocationName ?? ''))}
            InputProps={{
              startAdornment: MobileLocationSearchLeftIconAlternative ? (
                <MobileLocationSearchLeftIconAlternative
                  sx={{
                    color: theme.custom?.SearchFilters?.mobile?.textColor,
                    borderRadius: '50%',
                    ...baseAdornmentStyles
                  }}
                />
              ) : null,
              endAdornment: isLocationChanged ? <LoadingWheel style={{ marginRight: '10px' }} /> : null,
              sx: (themeIn) => ({ ...themeIn.typography.body1, padding: 0 })
            }}
          />
          <StyledMenuList style={{ zIndex: 99, overflow: 'hidden' }} isOpen={isLocationFocused}>
            <ItemRootContainer
              isMobile
              sx={{ justifyContent: 'space-between', alignItems: 'center' }}
              onClick={getYourLocation}
            >
              <ItemTitle sx={{ color: '#11245A' }}>{translate('homepage.search.location.current')}</ItemTitle>
              <MyLocationIcon sx={{ width: 14, height: 14, color: 'primary.main' }} />
            </ItemRootContainer>
            <ItemRootContainer sx={{ py: 0, minHeight: 0 }}>
              <Divider sx={{ width: '100%' }} />
            </ItemRootContainer>
            {mappedLocation.length ? (
              mappedLocation.map((location, index) => (
                <MenuItem
                  key={String(location.id) + index}
                  onClick={() => {
                    setIsLocationFieldFocused(false)
                    onLocationChanged(location)
                  }}
                  sx={{
                    fontSize: '14px',
                    padding: '16px 24px',
                    color: '#11245A',
                    boxSizing: 'border-box',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    whiteSpace: 'wrap',
                    backgroundColor: '#fff',
                    '&:hover': {
                      backgroundColor: theme.custom.SearchBar.MenuItem.backgroundColor
                    },
                    ...(decodeURIComponent(filters?.selectedLocationName ?? '') === location.label
                      ? theme.custom.SearchBar.SelectedMenuItem
                      : {})
                  }}
                >
                  {location.label}
                </MenuItem>
              ))
            ) : (
              <StyledNoOptionText>{translate('searchBar.noOption')}</StyledNoOptionText>
            )}
          </StyledMenuList>
        </div>
      </div>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Link
          component="button"
          onClick={() => setSearchBarExpand(false)}
          sx={{ color: '#2C3341', fontWeight: 500, textDecorationColor: '#2C3341', fontSize: '15px' }}
        >
          {translate('homepage.search.close')}
        </Link>
        <Button
          sx={{
            ...(theme?.custom?.SearchBar?.mobile?.searchButton ?? {}),
            background: '#1A3586',
            marginTop: 0,
            padding: '8px 24px'
          }}
          onClick={onSearch}
        >
          {isLoadingButtonState ? (
            <LoadingWheel />
          ) : (
            <Typography
              fontSize="15px"
              variant="h4"
              color={theme?.custom?.SearchBar?.mobile?.searchButton?.color ?? 'white'}
            >
              {translate('search')}
            </Typography>
          )}
        </Button>
      </Box>
    </Container>
  )
}

export default MobileFiltersModal
